import * as React from 'react'
import { Network as NetworkIcon } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  useGetPlantNetworkNodeDetailQuery,
  useGetPlantNetworkTreeQuery
} from '@axilon-apps/domains-ontology-ui-state'
import { Empty } from '../../components/primitives/Empty'
import { plantNetworkIndexHref } from '../../lib/plant-links'
import { NetworkDetailPanel } from './NetworkDetailPanel'
import { NetworkTreeNav } from './NetworkTreeNav'

/**
 * `/plant/network` — Network tab (Experion EMDB enterprise / PCN).
 *
 * Single page for both unselected (empty pane on the right) and selected
 * (`?node=<id>`) states. The left rail and the right detail panel stay
 * mounted so tree expand/collapse survives intra-tab navigation.
 *
 * URL contract: the selected node lives in `?node=` and is shared across
 * Assets, NetworkSegments, and NetworkDevices.
 */
const NetworkPage = (): React.ReactElement => {
  const [searchParams] = useSearchParams()
  // `URLSearchParams.get` already percent-decodes once. The node href builders
  // single-encode via `URLSearchParams.set`, so `get('node')` returns the exact
  // graph id — including ids that carry a literal `%2F` (e.g. an Experion asset
  // slug `Assets%2FB1`). A second `decodeURIComponent` here would collapse that
  // `%2F` to `/`, breaking both the detail lookup and the tree selection.
  const selectedNodeId = searchParams.get('node') || null

  const tree = useGetPlantNetworkTreeQuery()
  const nodes = tree.data?.nodes ?? []

  const detail = useGetPlantNetworkNodeDetailQuery(selectedNodeId ?? '', {
    skip: !selectedNodeId
  })

  const breadcrumbName = React.useMemo(() => {
    if (!detail.data) return selectedNodeId ?? ''
    switch (detail.data.kind) {
      case 'Asset':
        return detail.data.asset.display_name ?? detail.data.asset.name
      case 'NetworkSegment':
        return detail.data.segment.display_name ?? detail.data.segment.name
      case 'NetworkDevice':
        return detail.data.device.display_name ?? detail.data.device.name
    }
  }, [detail.data, selectedNodeId])

  return (
    <div className="flex flex-1 flex-col gap-card">
      {selectedNodeId ? (
        <nav className="text-sm text-ink-3" aria-label="Breadcrumb">
          <Link to={plantNetworkIndexHref()} className="hover:text-ink hover:underline">
            Network
          </Link>
          <span className="mx-1.5 text-ink-4">/</span>
          {detail.data && (
            <>
              <span className="text-ink-3">{detail.data.kind}</span>
              <span className="mx-1.5 text-ink-4">/</span>
            </>
          )}
          <span className="text-ink">{breadcrumbName}</span>
        </nav>
      ) : (
        <header>
          <h1 className="m-0 text-2xl font-semibold text-ink">Network</h1>
          <p className="mt-1 text-sm text-ink-3">{networkSubtitle(nodes)}</p>
        </header>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[17.5rem_minmax(0,1fr)]">
        <NetworkTreeNav
          nodes={nodes}
          isLoading={tree.isLoading}
          selectedId={selectedNodeId ?? undefined}
        />
        <div className="flex min-w-0 flex-col gap-card">
          {selectedNodeId ? (
            <NetworkDetailPanel nodeId={selectedNodeId} />
          ) : (
            <div className="flex min-h-80 flex-col rounded-xl border bg-card text-card-foreground shadow-sm">
              {tree.isError ? (
                <Empty
                  title="Couldn't load network tree"
                  hint="Check the proxy is reachable and try again."
                  icon={NetworkIcon}
                />
              ) : (
                <Empty
                  title="Select a network node"
                  hint="Pick an asset, network segment, or device from the tree to see its hierarchy, mapped compute, and cross-references."
                  icon={NetworkIcon}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function networkSubtitle(nodes: { kind: string }[]): string {
  if (nodes.length === 0) {
    return 'Enterprise and PCN assets, network segments, and the devices they contain.'
  }
  let assets = 0
  let segments = 0
  let devices = 0
  for (const node of nodes) {
    if (node.kind === 'Asset') assets += 1
    else if (node.kind === 'NetworkSegment') segments += 1
    else if (node.kind === 'NetworkDevice') devices += 1
  }
  const parts: string[] = [
    `${assets.toLocaleString()} ${assets === 1 ? 'asset' : 'assets'}`,
    `${segments.toLocaleString()} ${segments === 1 ? 'segment' : 'segments'}`,
    `${devices.toLocaleString()} ${devices === 1 ? 'device' : 'devices'}`
  ]
  return `${parts.join(' · ')}.`
}

export default NetworkPage
